import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SignalNetwork } from "./SignalNetwork";
import { MaskedLines } from "../motion/Reveal";
import { scrollToTarget } from "../layout/SmoothScroll";

const ease = [0.22, 1, 0.36, 1];

export const Hero = ({ ready }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-25%"]);

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="tc-spotlight relative isolate min-h-[100svh] overflow-hidden bg-[#0C0A09] pt-[104px] text-stone-100">
      <div className="tc-grid-bg absolute inset-0 -z-20 opacity-40" />
      <SignalNetwork className="absolute inset-0 -z-10 h-full w-full" />
      <div className="absolute -left-40 top-1/3 -z-10 h-[520px] w-[520px] rounded-full bg-orange-600/20 blur-[140px]" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-5 pb-16 pt-10 sm:px-8 lg:block lg:min-h-[calc(100svh-150px)] lg:px-12 lg:pt-16">
        <motion.div style={{ y: textY }} className="relative z-10 lg:pt-6">
          <motion.p initial={reduce ? false : { opacity: 0 }} animate={ready ? { opacity: 1 } : {}} transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.32em] text-orange-400">
            TechCare Technologies · Kenya
          </motion.p>
          <h1 className="mt-6 font-display font-semibold leading-[0.9] tracking-[-0.02em] text-[clamp(2.3rem,8.3vw,8.2rem)]" data-testid="hero-headline">
            <MaskedLines play={ready} delay={0.15} stagger={0.13}
              lines={["TECHNOLOGY", "THAT", <span key="c" className="tc-copper-text">CARES.</span>]} />
          </h1>
          <motion.p initial={reduce ? false : { opacity: 0, y: 16 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.75, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg" data-testid="hero-subcopy">
            Intelligent technology. Reliable infrastructure. Human-centred solutions. Building a more connected, secure and innovative future.
          </motion.p>
          <motion.div initial={reduce ? false : { opacity: 0, y: 16 }} animate={ready ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.9, duration: 0.9, ease }}
            className="mt-10 flex flex-col gap-3 sm:flex-row">
            <button onClick={() => scrollToTarget("#services")} data-testid="hero-explore-btn"
              className="tc-btn-primary group inline-flex h-14 items-center justify-center gap-3 rounded-full px-8 text-sm font-semibold">
              Explore Our Solutions <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <Link to="/contact?type=consultation" data-testid="hero-consult-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-full border border-white/20 px-8 text-sm font-semibold text-stone-100 transition-colors duration-300 hover:border-orange-500 hover:bg-white/5">
              Request a Consultation <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </motion.div>

        <div className="relative lg:absolute lg:bottom-16 lg:right-12 lg:top-36 lg:z-0 lg:w-[38%]">
          <motion.div initial={reduce ? false : { clipPath: "inset(100% 0 0 0 round 28px)" }}
            animate={ready ? { clipPath: "inset(0% 0 0 0 round 28px)" } : {}} transition={{ duration: 1.4, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
            className="relative aspect-[4/3] w-full overflow-hidden rounded-[28px] sm:aspect-[4/5] lg:aspect-auto lg:h-full">
            <motion.img style={{ y: imgY, scale: 1.18 }} src="/images/hero-nairobi-night.jpg" alt="Nairobi city skyline lit up at night"
              className="absolute inset-0 h-full w-full object-cover object-[center_70%] brightness-[1.18] contrast-[1.05] saturate-[1.1]" fetchPriority="high" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_50%,transparent_25%,rgba(12,10,9,0.62)_85%)]" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0C0A09]/80 to-transparent" />
            <div className="tc-scanlines absolute inset-0 opacity-[0.12]" />
            <div className="tc-scanbeam absolute inset-x-0 h-24" aria-hidden="true" />
            <div className="absolute right-5 top-5 flex items-end gap-[3px]" aria-hidden="true">
              {[6, 9, 12, 15].map((h, i) => <span key={h} className="tc-bar w-[3px] rounded-sm bg-orange-400" style={{ height: h, animationDelay: `${i * 0.15}s` }} />)}
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-200"><span className="mb-1.5 flex items-center gap-2"><span className="tc-pulse h-1.5 w-1.5 rounded-full bg-orange-500" /> Nairobi · KE</span>1.2921° S, 36.8219° E</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mx-auto hidden max-w-[1440px] items-center justify-between px-12 pb-8 font-mono text-[11px] uppercase tracking-[0.28em] text-stone-400 lg:flex">
        <span>Where Innovation Meets Care.</span>
        <span className="flex items-center gap-3"><span className="tc-pulse h-1.5 w-1.5 rounded-full bg-orange-500" /> Scroll to explore</span>
        <span>Kenya → Africa → International</span>
      </div>
    </section>
  );
};
